import React, { FunctionComponent } from 'react';
import { CopyToClipboardLink, CopyToClipboardButton } from 'app/components/utilities/CopyToClipboard';
import { IMeetingBookingState, BookingType, BookingState } from 'app/models/rtc/booking/constants';
import { useHistory } from 'app/components/router/History';
import { createMeetingBookingUrl } from 'app/models/rtc/booking/utils';
import { useCallBreakpoint } from 'app/containers/call/CallBreakpoint';
import { useI18n } from 'app/components/localization';
import { useCss } from 'app/utils/css';
import { sectionHeaderStyles } from 'app/components/presentation/text/styles';
import MarginGroup from 'app/components/layout/MarginGroup';
import { ExtractProps } from 'app/utils/types';

const enum MeetingLinkPlacement {
  /**
   * In the sidebar or below video element
   */
  Details,
  /**
   * Inside video element
   */
  Video,
}

const useMeetingLinkPlacement = (collapsed: boolean): MeetingLinkPlacement => {
  const { hasPeers } = useCallBreakpoint();

  return collapsed || hasPeers ? MeetingLinkPlacement.Details : MeetingLinkPlacement.Video;
};

export const useMeetingUrl = (bookingState?: BookingState) => {
  const history = useHistory();

  if (!bookingState || bookingState.bookingType !== BookingType.Meeting) {
    return null;
  }
  return history.getAbsoluteUrl(createMeetingBookingUrl(history, bookingState.bookingId));
};

interface ICopyButtonProps extends Pick<ExtractProps<typeof CopyToClipboardButton>, 'size' | 'colorScheme'> {
  collapsed?: boolean;
  url: string;
}

export const CopyButtonOrLink: FunctionComponent<ICopyButtonProps> = ({ collapsed, url, ...buttonProps }) => {
  const { t } = useI18n();

  const props = {
    value: url,
    label: t('meeting-booking-details.copy-link.copy'),
    copiedMessage: t('meeting-booking-details.copy-link.copied'),
  };

  return (
    collapsed
    ? <CopyToClipboardLink {...props} />
    : <CopyToClipboardButton
      {...props}
      {...buttonProps}
    />
  );
};

interface IProps {
  collapsed: boolean;
  bookingState: IMeetingBookingState;
}

export const CopyMeetingLinkVideoOverlay: FunctionComponent<IProps> = ({ collapsed, bookingState }) => {
  const placement = useMeetingLinkPlacement(collapsed);
  const url = useMeetingUrl(bookingState);
  const { t } = useI18n();
  const css = useCss();

  if (!url || placement !== MeetingLinkPlacement.Video) {
    return null;
  }

  return (
    <div
      className={css('container', {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      })}
    >
      <div className={css('message', { textAlign: 'center' })}>
        <MarginGroup>
          <div className={css('header', { fontSize: sectionHeaderStyles.fontSize })}>
            {t('meeting-booking-details.copy-link.empty-room')}
          </div>
          <div>
            {t('meeting-booking-details.copy-link.share')}
          </div>
          <CopyButtonOrLink url={url} />
        </MarginGroup>
      </div>
    </div>
  );
};
